import type { OAuthLoginData } from "@/betby/api/types";
import {
  applyLoginData,
  getAuthSnapshot,
  notifyTokenExpired,
} from "@/betby/api/httpRuntimeAdapter";
import { logger } from "@/betby/api/httpLogger";

let refreshTokenPromise: Promise<boolean> | null = null;

export function startTokenRefreshTimer(
  expiresTime: number | string,
  serverTime: number | string,
) {
  const exp = Number(expiresTime);
  const now = Number(serverTime);

  if (isNaN(exp) || isNaN(now)) {
    logger.warn("[startTokenRefreshTimer] 时间格式无效", {
      expiresTime,
      serverTime,
    });
    return;
  }

  const delay = exp - now - 60000;
  logger.info(
    `[startTokenRefreshTimer] Token 剩余有效期: ${delay}ms (已转为被动检测模式)`,
  );

  if (delay <= 0) {
    void refreshToken();
  }
}

export function checkAndRefreshToken(serverTime: number | string) {
  const { expiresTime } = getAuthSnapshot();
  if (!expiresTime) {
    return;
  }

  const exp = Number(expiresTime);
  const now = Number(serverTime);
  if (isNaN(exp) || isNaN(now)) {
    return;
  }

  const delay = exp - now - 60000;
  if (delay <= 0) {
    logger.info(
      `[checkAndRefreshToken] 检测到 Token 即将过期 (剩余 ${delay}ms)，触发刷新`,
    );
    void refreshToken();
  }
}

export async function refreshToken(): Promise<boolean> {
  if (refreshTokenPromise) {
    return refreshTokenPromise;
  }

  refreshTokenPromise = (async () => {
    try {
      const refreshTokenValue = getAuthSnapshot().refreshToken;
      if (!refreshTokenValue) {
        logger.warn("[refreshToken] 没有 refresh_token，无法刷新");
        return false;
      }

      logger.info("[refreshToken] 开始刷新 token...");
      const { oauthApi } = await import("@/betby/api/services");
      const response = await oauthApi.refresh(refreshTokenValue);
      const data = response.data as OAuthLoginData | undefined;

      if (response.code === 200 && data?.access_token && data?.refresh_token) {
        applyLoginData(data, response.timestamp);
        logger.info("[refreshToken] Token 刷新成功");
        return true;
      }

      logger.warn("[refreshToken] Token 刷新失败，响应:", response);
      if (response.code !== 401) {
        notifyTokenExpired(
          "We're sorry, an error occurred. Please try re-entering the sports play later.",
        );
      }
      return false;
    } catch (error) {
      logger.error("[refreshToken] Token 刷新异常:", error);
      notifyTokenExpired(
        "We're sorry, an error occurred. Please try re-entering the sports play later.",
      );
      return false;
    } finally {
      refreshTokenPromise = null;
    }
  })();

  return refreshTokenPromise;
}
